import './tuning.css';
import { t, tText, onLang } from './i18n';

/** One live-tunable parameter, reading/writing a value on some target object. */
export interface TuneParam {
  /** i18n key for the parameter's label. */
  labelKey: string;
  min: number;
  max: number;
  step: number;
  get(): number;
  set(v: number): void;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

/** Decimal places implied by a slider step (so 0.005 shows as 0.005, 10 as 10). */
function decimalsFor(step: number): number {
  return Math.max(0, Math.ceil(-Math.log10(step)));
}

/**
 * Collapsible developer panel exposing the tunable game parameters (physics
 * constants and control feel) as live sliders. Edits write straight through to
 * the underlying objects, so the next shot / input uses the new value.
 */
export class TuningPanel {
  private readonly panel = el('div', 'hud-panel interactive');
  private readonly body = el('div', 'tuning-body');

  constructor(root: HTMLElement, params: TuneParam[]) {
    this.panel.id = 'tuning-panel';

    const header = el('div', 'tuning-header');
    const title = el('span', 'tuning-title');
    tText(title, 'tuning.title');
    const collapse = el('button', 'tuning-collapse', '+');
    let open = false; // start collapsed
    this.body.style.display = 'none';
    collapse.onclick = () => {
      open = !open;
      this.body.style.display = open ? 'flex' : 'none';
      collapse.textContent = open ? '–' : '+';
    };
    header.append(title, collapse);

    for (const p of params) this.body.appendChild(this.buildRow(p));

    const reset = el('button', 'tuning-reset');
    tText(reset, 'tuning.reset');
    const defaults = params.map((p) => p.get());
    reset.onclick = () => {
      params.forEach((p, i) => p.set(defaults[i]));
      this.refresh();
    };
    this.body.appendChild(reset);

    this.panel.append(header, this.body);
    root.appendChild(this.panel);
  }

  private rows: (() => void)[] = [];

  private buildRow(p: TuneParam): HTMLElement {
    const row = el('div', 'tuning-row');
    const dec = decimalsFor(p.step);
    const label = el('div', 'tuning-label');
    const name = el('span');
    tText(name, p.labelKey);
    const value = el('span', 'tuning-value', p.get().toFixed(dec));
    label.append(name, value);

    const range = el('input', 'tuning-range');
    range.type = 'range';
    range.min = String(p.min);
    range.max = String(p.max);
    range.step = String(p.step);
    range.value = String(p.get());
    range.oninput = () => {
      const v = Number(range.value);
      p.set(v);
      value.textContent = v.toFixed(dec);
    };

    this.rows.push(() => {
      range.value = String(p.get());
      value.textContent = p.get().toFixed(dec);
    });

    row.append(label, range);
    return row;
  }

  /** Re-read every parameter into its slider (after a programmatic change/reset). */
  private refresh(): void {
    for (const r of this.rows) r();
  }
}
